import React from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDateShort } from "@/lib/dateUtils";
import type {
  CalibrationCheck,
  ReportDomain,
  ReportMetric,
  ScreeningReport,
} from "@/features/patients/domain/entities/ScreeningReport";
import {
  formatMetricNumber,
  summariseReport,
} from "@/features/patients/domain/utils/screeningReport";

// Reference band marks drawn on every percentile track.
const PERCENTILE_TICKS = [5, 10, 25, 75, 90, 95];

/** "72" + "nd pct" — suffix is an i18next ordinal plural. */
const Percentile: React.FC<{ value: number }> = ({ value }) => {
  const { t } = useTranslation("patients");
  const rounded = Math.round(value);
  return (
    <>
      <span className="font-semibold text-slate-900">{rounded}</span>
      <span className="ml-1 text-slate-500">
        {t("screeningReport.percentile", { count: rounded, ordinal: true })}
      </span>
    </>
  );
};

// ============================================================================
// Header
// ============================================================================

const ReportOverview: React.FC<{ report: ScreeningReport }> = ({ report }) => {
  const { t } = useTranslation("patients");

  const items: { label: string; value: React.ReactNode }[] = [
    {
      label: t("screeningReport.referenceRange"),
      value: (
        <>
          {report.referenceRange ?? "—"}
          {report.referenceBand && (
            <span className="ml-2 text-xs font-normal text-slate-600">
              ({t("screeningReport.comparedWithin", { band: report.referenceBand })})
            </span>
          )}
        </>
      ),
    },
    { label: t("screeningReport.device"), value: report.device ?? "—" },
    { label: t("screeningReport.assessedOn"), value: formatDateShort(report.assessedOn) },
    { label: t("screeningReport.generatedOn"), value: formatDateShort(report.generatedOn) },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-slate-200">
      {items.map((item) => (
        <div key={item.label} className="lg:px-6 lg:first:pl-0">
          <p className="text-sm text-slate-500">{item.label}</p>
          <p className="text-sm font-semibold text-slate-900">{item.value}</p>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Baseline & sensory calibration
// ============================================================================

const CalibrationRow: React.FC<{ check: CalibrationCheck }> = ({ check }) => {
  const { t } = useTranslation("patients");
  const thresholdMet =
    check.thresholdMet === undefined
      ? "—"
      : check.thresholdMet
        ? t("screeningReport.calibration.yes")
        : t("screeningReport.calibration.no");

  return (
    <tr className="border-b border-slate-200 align-top">
      <td className="px-4 py-3 font-semibold text-slate-900">
        {t(`screeningReport.calibration.tests.${check.key}.label`)}
      </td>
      <td className="px-4 py-3 text-slate-700">{check.observedValue ?? "—"}</td>
      <td className="px-4 py-3 font-semibold text-slate-900">{thresholdMet}</td>
      <td className="px-4 py-3 text-slate-700">
        {t(`screeningReport.calibration.tests.${check.key}.required`)}
      </td>
    </tr>
  );
};

const CalibrationCard: React.FC<{ report: ScreeningReport }> = ({ report }) => {
  const { t } = useTranslation("patients");

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
      <h4 className="text-lg font-semibold text-slate-900">
        {t("screeningReport.calibration.title")}
      </h4>
      <p className="mb-4 text-sm text-slate-500">{t("screeningReport.calibration.subtitle")}</p>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="bg-blue-50 text-left">
              <th className="rounded-l-lg px-4 py-3 font-semibold text-slate-900">
                {t("screeningReport.calibration.columns.test")}
              </th>
              <th className="px-4 py-3 font-semibold text-slate-900">
                {t("screeningReport.calibration.columns.observed")}
              </th>
              <th className="px-4 py-3 font-semibold text-slate-900">
                {t("screeningReport.calibration.columns.thresholdMet")}
              </th>
              <th className="rounded-r-lg px-4 py-3 font-semibold text-slate-900">
                {t("screeningReport.calibration.columns.required")}
              </th>
            </tr>
          </thead>
          <tbody>
            {report.calibration.map((check) => (
              <CalibrationRow key={check.key} check={check} />
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-sm">
        <span className="font-semibold text-slate-900">
          {t("screeningReport.calibration.adaptiveFlow")}
        </span>{" "}
        <span className="text-slate-600">{report.adaptiveFlowRecommendation ?? "—"}</span>
      </p>
    </section>
  );
};

// ============================================================================
// Metric row
// ============================================================================

const PercentileTrack: React.FC<{ percentile: number }> = ({ percentile }) => (
  <div className="relative h-4 w-full" aria-hidden>
    <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-slate-200" />
    {PERCENTILE_TICKS.map((tick) => (
      <span
        key={tick}
        className="absolute top-1/2 h-3 w-px -translate-y-1/2 bg-slate-400/70"
        style={{ left: `${tick}%` }}
      />
    ))}
    <span
      className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-2 ring-white"
      style={{ left: `${Math.min(100, Math.max(0, percentile))}%` }}
    />
  </div>
);

const MetricRow: React.FC<{ metric: ReportMetric }> = ({ metric }) => {
  const { t, i18n } = useTranslation("patients");

  const formattedValue = formatMetricNumber(metric, i18n.language);
  const unit = t(`screeningReport.units.${metric.unit}`);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 py-2 sm:grid-cols-[minmax(0,15rem)_7rem_minmax(0,1fr)_6rem]">
      <div className="flex min-w-0 items-center gap-1.5 text-sm text-slate-700">
        <span className="truncate">{t(`screeningReport.metrics.${metric.key}.label`)}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="shrink-0 text-slate-500 hover:text-slate-700"
              aria-label={t("screeningReport.metricInfo")}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {t(`screeningReport.metrics.${metric.key}.description`)}
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="text-right text-sm sm:text-left">
        <span className="font-semibold text-slate-900">{formattedValue}</span>
        {unit && <span className="ml-1 text-slate-500">{unit}</span>}
      </div>

      <div className="col-span-2 sm:col-span-1">
        <PercentileTrack percentile={metric.percentile} />
      </div>

      <div className="hidden text-right text-sm sm:block">
        <Percentile value={metric.percentile} />
      </div>
    </div>
  );
};

// ============================================================================
// Domain card
// ============================================================================

const DomainCard: React.FC<{ domain: ReportDomain }> = ({ domain }) => {
  const { t } = useTranslation("patients");

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
      <h4 className="mb-3 text-lg font-semibold text-slate-900">
        {t(`screeningReport.domains.${domain.key}`)}
      </h4>
      {domain.groups.map((group, index) => (
        <React.Fragment key={group.key ?? index}>
          {index > 0 && <Separator className="my-4" />}
          {group.key && (
            <p className="mb-1 text-sm font-semibold text-slate-500">
              {t(`screeningReport.groups.${group.key}`)}
            </p>
          )}
          <div className="flex flex-col">
            {group.metrics.map((metric) => (
              <MetricRow key={metric.key} metric={metric} />
            ))}
          </div>
        </React.Fragment>
      ))}
    </section>
  );
};

// ============================================================================
// Performance summary
// ============================================================================

const PerformanceSummary: React.FC<{ report: ScreeningReport }> = ({ report }) => {
  const { t } = useTranslation("patients");

  const { total, within, belowTypical } = summariseReport(report);

  return (
    <section className="rounded-xl bg-primary p-4 text-primary-foreground sm:p-5">
      <p className="font-semibold">{t("screeningReport.summary.title")}</p>
      <p className="text-sm">
        {t("screeningReport.summary.withinTypical", {
          within,
          total,
        })}
      </p>
      {belowTypical.length > 0 && (
        <>
          <p className="mt-3 font-semibold">{t("screeningReport.summary.belowTypical")}</p>
          <ul className="mt-1 list-disc pl-6 text-sm">
            {belowTypical.map(({ domain, metric }) => (
              <li key={`${domain}-${metric.key}`}>
                {t(`screeningReport.metrics.${metric.key}.label`)} (
                {t(`screeningReport.taskNames.${domain}`)}) — {Math.round(metric.percentile)}
                {t("screeningReport.percentile", {
                  count: Math.round(metric.percentile),
                  ordinal: true,
                })}
              </li>
            ))}
          </ul>
        </>
      )}
      <Separator className="my-3 bg-white/40" />
      <p className="text-sm">{t("screeningReport.summary.note")}</p>
    </section>
  );
};

// ============================================================================
// Report
// ============================================================================

const ScreeningReportView: React.FC<{ report: ScreeningReport }> = ({ report }) => {
  const { t } = useTranslation("patients");

  return (
    <div className="flex w-full flex-col gap-4 text-left">
      <ReportOverview report={report} />

      <div className="flex gap-3 rounded-lg bg-blue-900 p-4 text-sm text-white">
        <Info className="mt-0.5 h-5 w-5 shrink-0" />
        <p>
          <span className="font-semibold">{t("screeningReport.howToRead.title")}</span>{" "}
          {t("screeningReport.howToRead.body")}
        </p>
      </div>

      <CalibrationCard report={report} />

      {report.domains.map((domain) => (
        <DomainCard key={domain.key} domain={domain} />
      ))}

      <PerformanceSummary report={report} />

      <p className="rounded-lg bg-[#FFF4E5] p-4 text-sm text-[#E37400]">
        {t("screeningReport.disclaimer")}
      </p>

      <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <span className="font-semibold">{t("screeningReport.researchUseOnly.title")}</span>{" "}
        {t("screeningReport.researchUseOnly.body")}
      </p>

      <div className="mt-8 grid grid-cols-1 gap-8 pb-4 sm:grid-cols-2 sm:gap-12 sm:pr-[20%]">
        <p className="border-t border-slate-300 pt-2 text-sm text-slate-500">
          {t("screeningReport.clinicianSignature")}
        </p>
        <p className="border-t border-slate-300 pt-2 text-sm text-slate-500">
          {t("screeningReport.dateReviewed")}
        </p>
      </div>
    </div>
  );
};

export default ScreeningReportView;
